import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import ChannelBreakdownChart from './ChannelBreakdownChart';
import TotalChatsChart from './TotalChatsChart';
import TotalWebformsChart from './TotalWebformsChart';
import TotalCallsChart from './TotalCallsChart';
import TotalTextsChart from './TotalTextsChart';
import useFiltersState from '../reporting_utils/useFiltersState';
import ReportScheduler from '../reporting_utils/ReportScheduler';
import RefreshButton from '../reporting_utils/RefreshButton';
import DateRange from '../reporting_utils/DateRange';
import AcquisitionSummaryTable from './AcquisitionSummaryTable';
import { getFilterValues } from '../reporting_utils/misc';
import { Common } from '../../utils/common';
import ErrorBoundary from '../reporting_utils/ErrorBoundary';
import ReportFilters from '../reporting_utils/ReportFilters';
import CurrentDomains from '../reporting_utils/CurrentDomains';
import moment from 'moment';
import useDomainSettings from '../reporting_utils/useDomainSettings';
import EmptyReport from '../reporting_utils/EmptyReport';

const pageLoadingFrame = window.pageLoadingFrame;

function AcquisitionSummary({ controllerName, domainId }) {
  const [filters, setFilters] = useFiltersState({}, {}, domainId);
  const [sidebarIsOpen, setSidebarIsOpen] = useState(false);
  const [data, setData] = useState({
    tableData: [],
    pieData: [],
    callCount: 0,
    callData: [],
    leadCount: 0,
    leadData: [],
    chatCount: 0,
    chatData: [],
    textCount: 0,
    textData: [],
  });
  const [controlledDateRange, setControlledDateRange] = useState({
    start: moment(filters.start_date, 'YYYYMMDD'),
    end: moment(filters.end_date, 'YYYYMMDD'),
  });

  const { isAutomotiveIndustry, isTextingEnabled, isLoading } = useDomainSettings(
    filters.domains,
    filters.show_inactive
  );

  const fetchReportData = useCallback(async () => {
    if (!filters.domains) {
      return;
    }
    pageLoadingFrame('show');

    try {
      const res = await fetch('/acquisition_summary', {
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name=csrf-token]').content,
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify(getFilterValues(filters)),
      });

      const {
        summary_detail,
        summary_pie,
        summary_conversions_today,
        summary_conversions,
        summary_leads_today,
        summary_leads,
        summary_chats_today,
        summary_chats,
        summary_texts_today,
        summary_texts,
      } = await res.json();

      setData({
        tableData: summary_detail,
        pieData: summary_pie,
        callCount: summary_conversions_today[0].calls,
        callData: summary_conversions,
        leadCount: summary_leads_today[0].leads,
        leadData: summary_leads,
        chatCount: summary_chats_today[0].chats,
        chatData: summary_chats,
        textCount: summary_texts_today,
        textData: summary_texts,
      });
    } catch (error) {
      console.error(error);
      Common.notify('Failed to fetch report data', 'error');
    }
    pageLoadingFrame('hide');
  }, [filters]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  return (
    <div className='report-details-container'>
      <div className='report-details-header'>
        <div className='report-details-title'>
          <h1>Channel Overview</h1>
        </div>
        <div className='panel-header'>
          <ReportScheduler controllerName={controllerName} filters={filters} />
          <RefreshButton dataCallbacks={fetchReportData} />
          {!sidebarIsOpen && (
            <DateRange
              controlledDateRange={controlledDateRange}
              setControlledDateRange={setControlledDateRange}
              filters={filters}
              setFilters={setFilters}
            />
          )}
        </div>
      </div>
      <ReportFilters
        filters={filters}
        defaultFilterState={{}}
        open={sidebarIsOpen}
        setOpen={setSidebarIsOpen}
        setFilters={setFilters}
        controlledDateRange={controlledDateRange}
        setControlledDateRange={setControlledDateRange}
      />
      {filters.domains ? (
        <>
          <div className='applied-filters-container'>
            <div style={{ marginBottom: '20px' }}>
              <button className='btn btn-primary' onClick={() => setSidebarIsOpen(!sidebarIsOpen)}>
                <span className='fa fa-plus' /> Add Filters
              </button>
            </div>
            {!Common.in_crm_mode() && <CurrentDomains filters={filters} setFilters={setFilters} />}
          </div>
          {isTextingEnabled ? (
            <div className='row'>
              <div className='col-xs-12 col-lg-3'>
                <div className='col-xs-12'>
                  <ChannelBreakdownChart data={data.pieData} />
                </div>
              </div>
              <div className='col-xs-12 col-lg-9'>
                <div className='col-xs-12 col-lg-3'>
                  <TotalTextsChart data={data.textData} count={data.textCount} />
                </div>
                <div className='col-xs-12 col-lg-3'>
                  <TotalChatsChart data={data.chatData} count={data.chatCount} />
                </div>
                <div className='col-xs-12 col-lg-3'>
                  <TotalWebformsChart data={data.leadData} count={data.leadCount} />
                </div>
                <div className='col-xs-12 col-lg-3'>
                  <TotalCallsChart data={data.callData} count={data.callCount} />
                </div>
              </div>
            </div>
          ) : (
            <div className='row'>
              <div className='col-xs-12 col-sm-6 col-lg-3'>
                <ChannelBreakdownChart data={data.pieData} />
              </div>
              <div className='col-xs-12 col-sm-6 col-lg-3'>
                <TotalChatsChart data={data.chatData} count={data.chatCount} />
              </div>
              <div className='col-xs-12 col-sm-6 col-lg-3'>
                <TotalWebformsChart data={data.leadData} count={data.leadCount} />
              </div>
              <div className='col-xs-12 col-sm-6 col-lg-3'>
                <TotalCallsChart data={data.callData} count={data.callCount} />
              </div>
            </div>
          )}
          <ErrorBoundary>
            {isLoading ? null : (
              <AcquisitionSummaryTable
                key={[isAutomotiveIndustry, isTextingEnabled]}
                isAutomotiveIndustry={isAutomotiveIndustry}
                isTextingEnabled={isTextingEnabled}
                tableData={data.tableData}
              />
            )}
          </ErrorBoundary>
        </>
      ) : (
        <EmptyReport onClick={() => setSidebarIsOpen(!sidebarIsOpen)} />
      )}
    </div>
  );
}

AcquisitionSummary.propTypes = {
  controllerName: PropTypes.string.isRequired,
  domainId: PropTypes.string,
};

export default AcquisitionSummary;
